import { JobDetailsSkeleton } from "components/job/JobDetailsSkeleton";
import { useCallback, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { setPage, setSelectedJob } from "store/root.store";
import { getCachePrefill } from "store/slices/cache.store";
import {
  getComments,
  getFiles,
  getJob,
  getNotes,
  getVisits,
  initializeVisit,
  selectJobLoaded,
  selectVisitLoaded,
  setJobTab,
  setSelectedJob as setVisitSelectedJob,
} from "store/slices/jobs.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";
import { isFlagEnabled } from "store/slices/user.store";

interface JobProviderProps {
  jobId: string | undefined;
}

export default function JobProvider({ jobId }: JobProviderProps) {
  const dispatch = useAppDispatch();
  const jobLoaded = useAppSelector(selectJobLoaded);
  const visitLoaded = useAppSelector(selectVisitLoaded);
  const { jobs } = useAppSelector((state) => state.jobs);
  const { loading: cacheLoading } = useAppSelector((state) => state.cache);
  const commentsEnabled = useAppSelector((s) => isFlagEnabled(s, "Comments"));

  const { state }: any = useLocation();

  const initializeJob = useCallback(async () => {
    await dispatch(getJob()).catch(() => {
      dispatch(addSnackbarMessage({ key: "GetJob-fail" }));
    });
    dispatch(getFiles());
    dispatch(getNotes());
    dispatch(getVisits()).catch(() => {
      dispatch(addSnackbarMessage({ key: "GetJobVisits-fail" }));
    });
    if (commentsEnabled) dispatch(getComments());
  }, [commentsEnabled, dispatch]);

  const setJobId = useCallback(async () => {
    dispatch(setPage({ page: "jobs" }));
    await dispatch(setVisitSelectedJob({ jobId }));
    dispatch(setSelectedJob({ jobId }));
  }, [dispatch, jobId]);

  useEffect(() => {
    const job = jobId ? jobs[jobId] : null;
    if (!jobLoaded || !job || cacheLoading.prefill) return;
    dispatch(
      initializeVisit({
        job,
      }),
    );
  }, [cacheLoading.prefill, dispatch, jobId, jobLoaded, jobs]);

  useEffect(() => {
    if (!jobId) return;
    setJobId();
    initializeJob();
    dispatch(setJobTab({ tab: "details" }));
  }, [dispatch, initializeJob, jobId, setJobId]);

  useEffect(() => {
    dispatch(getCachePrefill({ force: false }));
  }, [dispatch]);

  useEffect(() => {
    if (!jobLoaded || !visitLoaded) return;
    if (state?.openComment) dispatch(setJobTab({ tab: "comments" }));
  }, [dispatch, jobLoaded, state?.openComment, visitLoaded]);

  if (!jobLoaded || !visitLoaded) {
    return <JobDetailsSkeleton />;
  }

  return <Outlet />;
}
